import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowDown, CircleAlert, Send } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import {
  useFiveSBoard,
  useFiveSTemplate,
  useMeta,
  useEvaluation,
  useSaveEvaluation,
  useShifts,
  useWorkCenters,
} from '@/lib/queries'
import { Hint } from '@/components/Hint'
import { ApiError } from '@/lib/api'
import {
  Badge,
  Button,
  Card,
  CardTitle,
  Checkbox,
  DateInput,
  EmptyState,
  Field,
  Modal,
  Select,
  Spinner,
  Textarea,
  todayISO,
} from '@/components/ui'
import { WorkerPicker } from '@/components/WorkerPicker'
import { FiveSMachinePicker } from '@/components/FiveSMachinePicker'
import { ScoreChip, StatusChip } from '@/components/FiveSBoard'
import { scoreColor } from '@/lib/fiveSTarget'
import { cn, formatPercent } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'
import type { FiveSBoardMachine } from '@/types/api'

/**
 * Scores in a form two copies of the same walk compare equal in.
 *
 * Sorted, because a walk seeded from the server and the same walk tapped in by
 * hand hold their keys in different orders, and `JSON.stringify` would call
 * those two different.
 */
function snapshot(scores: Record<string, number>): string {
  return JSON.stringify(Object.entries(scores).sort(([a], [b]) => a.localeCompare(b)))
}

/** The id a criterion's row is found by, for the "next unscored" jump. */
const rowId = (itemId: string) => `kriterij-${itemId}`

export function FiveSPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const t = useT()
  const meta = useMeta()
  const workCenters = useWorkCenters()
  const save = useSaveEvaluation()
  const [params, setParams] = useSearchParams()

  /*
   * The month's board, for the machine picker: what is still due goes first,
   * and each machine carries its last score. Shared with `/5s`, so the two
   * agree about what is left — and a submitted walk refreshes both.
   */
  const board = useFiveSBoard()

  /*
   * The same screen writes a walk and corrects one.
   *
   * `/5s/:id/izmjena` loads an existing evaluation into it; `/5s/nova` starts
   * empty. One form rather than two, because the second would be this file
   * again — seventy-three criteria, the running total, the unsaved-work guard
   * — with a different verb, and the two would drift on the first change.
   *
   * The save is unchanged: it posts to the same endpoint, which upserts on
   * (work centre, day), so an edit lands on the row it came from.
   */
  const { id: editingId } = useParams<{ id: string }>()
  const editing = useEvaluation(editingId)

  /*
   * Preselected from `?workCenterId=` — what "Ocijeni" on the board and a
   * scanned sticker both link to — so the walk starts on the machine the
   * evaluator is standing at instead of on a list of all of them.
   */
  const [workCenterId, setWorkCenterId] = useState(() =>
    editingId ? '' : (params.get('workCenterId') ?? ''),
  )

  /*
   * The day of the walk. Defaults to today, which is when almost every
   * evaluation is entered — somebody catching up on last week changes it.
   */
  const [evaluatedOn, setEvaluatedOn] = useState(todayISO())

  /*
   * Which shift was keeping the centre.
   *
   * Empty string is a real choice — "no shift recorded" — and not a missing
   * one, which is why the field is never required. A customer who has just
   * defined shifts has evaluators who have not been told yet, and refusing
   * their walk over a dropdown they have never seen would be worse than
   * recording it without.
   */
  const [shiftId, setShiftId] = useState('')

  const shifts = useShifts()

  /*
   * Applied once, and only to a walk being started.
   *
   * The server suggests the shift covering the caller's clock, which is right
   * almost every time because a walk is nearly always entered while it is being
   * done. It is a suggestion: the ref makes sure that clearing it back to "no
   * shift" sticks, instead of the next render helpfully putting it back.
   */
  const suggestionApplied = useRef(false)

  useEffect(() => {
    if (editingId || suggestionApplied.current) return

    const suggested = shifts.data?.suggestedId
    if (!suggested) return

    suggestionApplied.current = true
    setShiftId(suggested)
  }, [editingId, shifts.data?.suggestedId])

  /** Who was keeping the centre. Presence, not assessment — see WorkerPicker. */
  const [workerIds, setWorkerIds] = useState<string[]>([])

  const [comment, setComment] = useState('')
  const [scores, setScores] = useState<Record<string, number>>({})

  /** Narrows a long checklist to what is still missing before submitting. */
  const [onlyUnanswered, setOnlyUnanswered] = useState(false)

  /** A work centre the evaluator picked that would discard existing scores. */
  const [pendingCenter, setPendingCenter] = useState<string | null>(null)

  const template = useFiveSTemplate(workCenterId || undefined)

  const items = useMemo(
    () => template.data?.template.categories.flatMap((category) => category.items) ?? [],
    [template.data],
  )

  // Running total, so the evaluator sees the score forming as they walk the
  // machine rather than only after saving.
  const totals = useMemo(() => {
    const scaleMax = template.data?.template.scaleMax ?? 1
    const answered = items.filter((item) => scores[item.id] !== undefined)
    const total = answered.reduce((sum, item) => sum + scores[item.id], 0)
    const max = items.length * scaleMax

    return {
      total,
      max,
      answered: answered.length,
      remaining: items.length - answered.length,
      percentage: max > 0 ? (total / max) * 100 : 0,
    }
  }, [items, scores, template.data])

  /*
   * The machines to pick from.
   *
   * The board when it answers; otherwise the plain list of machines, with
   * nothing known about them. A walk is entered on plant wifi, and a board
   * that failed to load must not leave the evaluator with nothing to tap.
   */
  const machines = useMemo<FiveSBoardMachine[]>(() => {
    if (board.data) return board.data.workCenters
    if (!board.isError) return []

    return (workCenters.data ?? []).map((centre) => ({
      id: centre.id,
      name: centre.name,
      code: centre.code,
      type: centre.type,
      plant: centre.plant ?? null,
      hasTemplate: true,
      isMine: false,
      status: 'due',
      walk: null,
      draft: null,
      last: null,
      trend: [],
    }))
  }, [board.data, board.isError, workCenters.data])

  const chosen = machines.find((machine) => machine.id === workCenterId)

  /*
   * Which month the chosen date files this evaluation under.
   *
   * Shown because the period is no longer something the evaluator picks, and a
   * walk done on the 1st of September for the August state of a machine would
   * otherwise be filed under September with nothing on screen saying so.
   */
  const periodHint = useMemo(() => {
    if (!evaluatedOn) return undefined

    const [y, m] = evaluatedOn.split('-').map(Number)
    // The month name is the server's, from /api/meta — interpolated into our
    // sentence rather than glued to it, so the word order can move.
    const monthName = meta.data?.months.find((option) => option.value === m)?.label

    return monthName ? t('fiveS.periodHint', { month: monthName, year: y }) : undefined
  }, [evaluatedOn, meta.data, t])

  /*
   * The scores as the server last accepted them.
   *
   * The guard below used to fire on any scores at all, so a walk saved as a
   * draft still asked "leave the page?" on the way out — the same question it
   * asks about a walk that would really be lost. Somebody told that twice about
   * work that was safe stops reading it.
   */
  const [savedScores, setSavedScores] = useState(() => snapshot({}))

  const hasUnsavedScores = Object.keys(scores).length > 0 && snapshot(scores) !== savedScores

  /*
   * Seed the form from the evaluation being corrected.
   *
   * Keyed on its id rather than the object: every refetch returns a new one,
   * and re-seeding on that would wipe whatever has been re-scored the moment a
   * background refresh lands — on a screen where the state is a walk of a
   * machine that cannot be repeated from memory.
   */
  useEffect(() => {
    const evaluation = editing.data

    if (!evaluation) return

    setWorkCenterId(evaluation.workCenter?.id ?? '')
    setEvaluatedOn(evaluation.evaluatedOn?.slice(0, 10) ?? todayISO())
    setShiftId(evaluation.shift?.id ?? '')
    setWorkerIds(evaluation.workers?.map((worker) => worker.id) ?? [])
    setComment(evaluation.comment ?? '')

    const seeded = Object.fromEntries(
      (evaluation.answers ?? []).map((answer) => [answer.itemId, answer.score]),
    )

    setScores(seeded)
    // What came from the server is saved by definition.
    setSavedScores(snapshot(seeded))
  }, [editing.data?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Warn before the tab closes on unsaved scoring.
   *
   * An evaluation is up to 73 individual taps collected while walking a machine.
   * There is a draft button, but nobody remembers to press it before closing a
   * tab, and the whole walk has to be repeated if they do not.
   */
  useEffect(() => {
    if (!hasUnsavedScores) return

    const handler = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', handler)

    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedScores])

  if (!user?.canEvaluate) {
    return (
      <Card>
        <EmptyState
          title={t('fiveS.denied.title')}
          description={t('fiveS.denied.description')}
        />
      </Card>
    )
  }

  /*
   * Changing the work centre loads a different template, so the scores cannot
   * carry over. Previously they were dropped without a word — one stray tap on
   * the dropdown erased an entire walk-through. Now it asks first.
   */
  function requestCenterChange(nextId: string) {
    if (nextId === workCenterId) return

    if (Object.keys(scores).length > 0) {
      setPendingCenter(nextId)
      return
    }

    applyCenterChange(nextId)
  }

  /*
   * Clear the walk for the next machine.
   *
   * The date and the shift stay, because the evaluator is almost always
   * walking the next machine on the same day in the same shift; everything
   * that belongs to one machine — the machine, its scores, who was keeping it,
   * the comment — goes. Back to the top, where the machine is chosen: on a
   * phone the submit button is seventy-odd criteria below it.
   */
  function startNextWalk() {
    applyCenterChange('')
    setSavedScores(snapshot({}))
    setWorkerIds([])
    setComment('')

    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: still ? 'auto' : 'smooth' })
  }

  function applyCenterChange(nextId: string) {
    setWorkCenterId(nextId)
    setScores({})
    setOnlyUnanswered(false)
    setPendingCenter(null)

    // The link that preselected the last machine no longer describes the
    // screen; a reload should not put it back.
    if (params.has('workCenterId')) setParams({}, { replace: true })
  }

  /*
   * Down to the first criterion nobody has scored yet.
   *
   * Seventy-three criteria on a phone is a long scroll, and a walk that will
   * not submit because of one missed line used to leave the evaluator hunting
   * for an outlined row. Focus lands on its first score button, so the next
   * tap scores it.
   */
  function jumpToUnscored() {
    const next = items.find((item) => scores[item.id] === undefined)
    if (!next) return

    const row = document.getElementById(rowId(next.id))
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    row?.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' })
    row?.querySelector<HTMLButtonElement>('[role=radio]')?.focus({ preventScroll: true })
  }

  async function handleSave(status: 'DRAFT' | 'SUBMITTED') {
    if (!workCenterId) return

    if (status === 'SUBMITTED' && totals.remaining > 0) {
      toast.error(t('fiveS.submit.blocked', { count: totals.remaining }))
      // Point them at exactly what is missing instead of leaving them to hunt.
      jumpToUnscored()
      return
    }

    try {
      const saved = await save.mutateAsync({
        workCenterId,
        evaluatedOn,
        // Always sent from this screen, including empty — the picker is right
        // here, so an empty list genuinely means nobody was recorded.
        workerIds,
        // Empty means "no shift", which the server stores as such. `undefined`
        // would mean the same thing here, but only by accident — an absent key
        // is how the offline queue's older payloads look, and the two should
        // not have to be told apart.
        shiftId: shiftId || undefined,
        comment: comment || undefined,
        status,
        answers: items
          .filter((item) => scores[item.id] !== undefined)
          .map((item) => ({ itemId: item.id, score: scores[item.id] })),
      })

      if (status === 'DRAFT') {
        // A draft is continued on this screen, so it stays — only now leaving
        // it no longer warns about work that is already saved.
        setSavedScores(snapshot(scores))
        toast.success(t('fiveS.draftSaved'))

        return
      }

      const score = formatPercent(saved.scorePercentage)

      /*
       * A submitted walk is finished, so the screen has to say so by moving on.
       *
       * It used to stay exactly as it was: the same machine, the same scores,
       * "Predaj" still lit, and the leave-page warning still armed. Livprom's
       * evaluators pressed it, saw nothing change, and had to leave and come
       * back to start the next machine — "ne prebaci nas automatski na novo
       * ocjenjivanje". A submitted evaluation cannot be changed anyway, so
       * leaving it on screen offered nothing but a second press.
       *
       * A correction goes to the evaluation it corrected; a new walk makes way
       * for the next one — the picker, with what is still due at the top — and
       * the one just submitted is a tap away in the toast.
       */
      if (editingId) {
        toast.success(t('fiveS.submitted', { score }))
        navigate(`/5s/${saved.id}`, { replace: true })

        return
      }

      startNextWalk()

      toast.success(t('fiveS.submitted.next', { score }), {
        action: {
          label: t('fiveS.submitted.open'),
          onClick: () => navigate(`/5s/${saved.id}`),
        },
      })
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t('fiveS.saveFailed'))
    }
  }

  const scaleMax = template.data?.template.scaleMax ?? 5

  // Absent, not empty: a plant that works one shift has nothing to pick.
  const hasShifts = (shifts.data?.data.length ?? 0) > 0

  const machineName = chosen?.name ?? template.data?.workCenter.name ?? editing.data?.workCenter?.name

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold sm:text-2xl">{t('fiveS.title')}</h1>
        {/*
          The one rule that decides whether this walk lands on its own record or
          on top of somebody else's, one tap away rather than a paragraph above
          the form. The server still says it as a 422 to anybody who misses it.
        */}
        <p className="muted flex items-center gap-1.5 text-sm">
          {t('fiveS.walk.subtitle')}
          <Hint side="bottom">{t('fiveS.help.oneWalk')}</Hint>
        </p>
      </header>

      {!workCenterId && !editingId ? (
        <FiveSMachinePicker
          machines={machines}
          loading={board.isPending || (board.isError && workCenters.isPending)}
          onPick={requestCenterChange}
          onContinue={(evaluationId) => navigate(`/5s/${evaluationId}/izmjena`)}
        />
      ) : (
        <>
          {/* The machine being walked, and the way to another one. */}
          <Card className="flex-row flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="muted text-xs">{t('fiveS.workCenter')}</p>
              <p className="truncate text-lg font-semibold">{machineName ?? '…'}</p>
              {chosen && (
                <p className="muted flex flex-wrap items-center gap-1.5 text-xs">
                  {[chosen.plant?.name, chosen.code].filter(Boolean).join(' · ')}
                  {chosen.last && (
                    <>
                      <span aria-hidden>·</span>
                      {t('fiveS.picker.last')}
                      <ScoreChip value={chosen.last.scorePercentage} />
                      {chosen.last.label}
                    </>
                  )}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {chosen && chosen.status !== 'due' && <StatusChip status={chosen.status} />}
              {/*
                Not while correcting a walk: the correction is of *that*
                machine's sheet, and moving it to another would file a second
                walk and leave the draft behind.
              */}
              {!editingId && (
                <Button variant="secondary" onClick={() => requestCenterChange('')}>
                  {t('fiveS.picker.change')}
                </Button>
              )}
            </div>
          </Card>

          {/*
            Four fields when the customer works in shifts and three when they do
            not, rather than a fixed grid with a gap in it. The picker is absent
            rather than disabled for a plant that has defined none — an empty
            dropdown is a question with no answer.
          */}
          <Card className={cn('grid gap-4', hasShifts ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
            {/*
              One date instead of the month and year dropdowns it replaced.
              The period is derived from it on the server, so the two can no longer
              disagree — and it records *when the walk happened*, which a month on
              its own cannot: the 3rd and the 28th describe very different months.
            */}
            <Field
              label={t('fiveS.date')}
              required
              hint={periodHint}
              action={<Hint>{t('fiveS.date.help')}</Hint>}
            >
              <DateInput
                value={evaluatedOn}
                onChange={(e) => setEvaluatedOn(e.target.value)}
                // Not in the future: this records what somebody saw.
                max={todayISO()}
                required
              />
            </Field>

            {hasShifts && (
              <Field
                label={t('fiveS.shift')}
                hint={t('fiveS.shift.hint')}
                action={<Hint>{t('fiveS.shift.help')}</Hint>}
              >
                <Select value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
                  {/*
                    A named option rather than a blank one. "Bez smjene" is a
                    choice somebody can mean; an empty row reads as a dropdown that
                    has not finished loading.
                  */}
                  <option value="">{t('fiveS.shift.none')}</option>
                  {shifts.data?.data.map((shift) => (
                    <option key={shift.id} value={shift.id}>
                      {shift.window ? `${shift.name} · ${shift.window}` : shift.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field
              label={t('fiveS.workers')}
              hint={t('fiveS.workers.hint')}
              action={<Hint>{t('fiveS.workers.help')}</Hint>}
            >
              <WorkerPicker
                value={workerIds}
                onChange={setWorkerIds}
                // Narrows the list to the people who work this machine.
                workCenterId={workCenterId || undefined}
              />
            </Field>
          </Card>
        </>
      )}

      {template.isLoading && <Spinner label={t('fiveS.template.loading')} />}

      {template.isError && (
        <Card>
          <EmptyState
            title={t('fiveS.template.missing')}
            description={(template.error as Error).message}
          />
        </Card>
      )}

      {template.data && (
        /*
         * One column up to xl, checklist plus a pinned summary beyond it. The
         * checklist is the long thing; the summary is the running total and
         * the two buttons, which should not need scrolling back to.
         */
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-4">
            {totals.remaining > 0 && (
              <Checkbox
                checked={onlyUnanswered}
                onChange={(e) => setOnlyUnanswered(e.target.checked)}
                label={t('fiveS.onlyUnanswered', { count: totals.remaining })}
                hint={t('fiveS.onlyUnanswered.hint')}
              />
            )}

            {template.data.template.categories.map((category) => {
              const visible = onlyUnanswered
                ? category.items.filter((item) => scores[item.id] === undefined)
                : category.items

              const answeredHere = category.items.filter(
                (item) => scores[item.id] !== undefined,
              ).length

              // Nothing left to show in this category while filtering.
              if (visible.length === 0) return null

              return (
                <Card key={category.id}>
                  <CardTitle
                    action={
                      <Badge
                        color={answeredHere === category.items.length ? 'emerald' : 'slate'}
                      >
                        {answeredHere} / {category.items.length}
                      </Badge>
                    }
                  >
                    {category.name}
                  </CardTitle>

                  <ul className="space-y-3">
                    {visible.map((item) => {
                      const answered = scores[item.id] !== undefined

                      return (
                        <li
                          key={item.id}
                          id={rowId(item.id)}
                          // Clears the header when jumped to.
                          className="flex scroll-mt-24 flex-col gap-2 border-b border-[var(--border-subtle)] pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                        >
                          <div className="min-w-0">
                            <span className="text-sm">{item.criterion}</span>

                            {/* The template's guidance, which the admin screen
                                calls "help for the evaluator" and which the
                                evaluator could not actually see until now. */}
                            {item.guidance && (
                              <span className="muted mt-0.5 block text-xs">{item.guidance}</span>
                            )}

                          </div>

                          {/*
                            Buttons, not a dropdown. A supervisor scoring 73
                            criteria with one hand should tap once per
                            criterion, not open a select, scroll and confirm.

                            On a phone they share the full width of the row,
                            so each is as wide as a thumb rather than a 44px
                            square at the far edge; at a desk they close up
                            beside the criterion.
                          */}
                          <div
                            className={cn(
                              'grid w-full shrink-0 gap-1.5 rounded-xl sm:flex sm:w-auto sm:flex-wrap',
                              // An unscored criterion is outlined, so what is
                              // left stands out while walking the machine.
                              !answered && 'ring-1 ring-amber-400/70',
                            )}
                            style={{ gridTemplateColumns: `repeat(${scaleMax}, minmax(0, 1fr))` }}
                            role="radiogroup"
                            aria-label={item.criterion}
                          >
                            {Array.from({ length: scaleMax }, (_, index) => index + 1).map(
                              (value) => {
                                const selected = scores[item.id] === value

                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    /*
                                     * Functional updater, not `{ ...scores, … }`.
                                     * A supervisor works down 73 criteria at
                                     * speed. The spread form reads `scores` as it
                                     * was at render time, so two taps landing in
                                     * the same React batch would lose one — and a
                                     * silently dropped score changes the work
                                     * center's percentage.
                                     */
                                    onClick={() =>
                                      setScores((current) => ({ ...current, [item.id]: value }))
                                    }
                                    className={cn(
                                      'min-h-control rounded-xl border text-base font-semibold tabular-nums transition-colors sm:size-control sm:text-sm',
                                      selected
                                        ? 'bg-brand-600 border-brand-600 text-white'
                                        : 'border-[var(--border-subtle)] hover:bg-[var(--surface-sunken)]',
                                    )}
                                  >
                                    {value}
                                  </button>
                                )
                              },
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </Card>
              )
            })}

            <Card>
              <Field label={t('fiveS.comment')} hint={t('fiveS.comment.hint')}>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={t('fiveS.comment.placeholder')}
                />
              </Field>
            </Card>
          </div>

          {/* Pinned beside the list on a wide screen, pinned to the bottom of
              the viewport on a narrow one. bottom-20 clears the mobile nav. */}
          <div className="sticky bottom-20 z-10 xl:top-6 xl:bottom-auto">
            <Card className="space-y-2 shadow-lg xl:space-y-3">
              <div className="flex items-center justify-between gap-3">
                {/*
                  How much of the walk is done, as a count of criteria — the
                  question somebody halfway round a machine has. The score
                  forming beside it is the other one.
                */}
                <p className="font-semibold tabular-nums" aria-live="polite">
                  {t('fiveS.progress', { answered: totals.answered, total: items.length })}
                </p>

                {/*
                  The percentage carries the explanation, not the raw total
                  beside it: a percentage on a screen that has just shown a 1–3
                  scale is the number people ask about.
                */}
                <span className="flex items-center gap-1">
                  <Badge color={scoreColor(totals.percentage)} className="tabular-nums">
                    {formatPercent(totals.percentage)}
                  </Badge>
                  <Hint side="left">{t('fiveS.percentage.help')}</Hint>
                </span>
              </div>

              {/* Progress by criteria answered, not by score — this bar is about
                  how much of the walk is done, not how well the centre scored. */}
              <div
                className="bg-muted h-2 overflow-hidden rounded-full"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={items.length}
                aria-valuenow={totals.answered}
                aria-label={t('fiveS.progressLabel')}
              >
                <div
                  className="bg-brand-600 h-full rounded-full transition-[width]"
                  style={{
                    width: `${items.length > 0 ? (totals.answered / items.length) * 100 : 0}%`,
                  }}
                />
              </div>

              {/* Two equal halves on a phone, where the pinned card has 311px
                  for both buttons; stacked in the side panel at a desk. */}
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-1">
                <Button
                  variant="secondary"
                  onClick={() => handleSave('DRAFT')}
                  loading={save.isPending}
                  className="min-w-0"
                >
                  {t('fiveS.saveDraft')}
                </Button>

                {/*
                  One primary button that says what to do next. While anything
                  is unscored that is "go to it", with the count left on the
                  button; once everything is, it is "Predaj". A disabled
                  "Predaj" with a sentence under it said the same thing slower,
                  and left the evaluator to find the missing line.
                */}
                <span className="flex min-w-0 items-center gap-1.5">
                  {totals.remaining > 0 ? (
                    <Button
                      fullWidth
                      onClick={jumpToUnscored}
                      aria-label={t('fiveS.jump.aria', { count: totals.remaining })}
                    >
                      <ArrowDown className="size-4" aria-hidden />
                      {t('fiveS.jump', { count: totals.remaining })}
                    </Button>
                  ) : (
                    <Button fullWidth onClick={() => handleSave('SUBMITTED')} loading={save.isPending}>
                      <Send className="size-4" aria-hidden />
                      {t('fiveS.submit')}
                    </Button>
                  )}
                  <Hint side="left">{t('fiveS.submit.help')}</Hint>
                </span>
              </div>

              {totals.remaining > 0 && (
                <p className="muted hidden items-start gap-1.5 text-xs xl:flex">
                  <CircleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  {t('fiveS.submit.hint')}
                </p>
              )}
            </Card>
          </div>
        </div>
      )}

      <Modal
        open={pendingCenter !== null}
        onClose={() => setPendingCenter(null)}
        title={t('fiveS.centerChange.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingCenter(null)}>
              {t('fiveS.centerChange.stay')}
            </Button>
            <Button variant="danger" onClick={() => applyCenterChange(pendingCenter ?? '')}>
              {t('fiveS.centerChange.confirm')}
            </Button>
          </>
        }
      >
        {/* One sentence, one key. The count used to be bolded mid-sentence,
            which meant splitting the warning into fragments around a number —
            and a fragment ending in a numeral is exactly what these languages
            inflect the following noun for. */}
        <p className="text-sm">
          {t('fiveS.centerChange.body', { count: Object.keys(scores).length })}
        </p>
        <p className="muted mt-2 text-sm">{t('fiveS.centerChange.keepHint')}</p>
      </Modal>
    </div>
  )
}
